/**
 * GhostSyncService (Refactored)
 *
 * E018-S009: Reconciliation service that syncs required ghosts with actual ghosts.
 * E018-SOLID: Now uses SOLID-compliant infrastructure:
 *   - IGhostPropertyMapper for property mapping and diffing
 *   - IGhostPositionStrategy for position calculation
 *
 * This class now ONLY handles reconciliation logic (SRP).
 * Property mapping and position calculation are delegated to injected services.
 *
 * Line count: ~120 (was: 280) - 57% reduction!
 */

import type { Bound } from '@blocksuite/affine/global/gfx';
import { parseXYWH } from '@blocksuite/affine/global/gfx';
import type { Store } from '@blocksuite/store';

import type {
  IGhostPositionStrategy,
  PositionContext,
} from '../core/interfaces/IGhostPositionStrategy';
import type { GhostBlockPropsInput } from '../core/interfaces/IGhostPropertyMapper';
import type { IGhostPropertyMapper } from '../core/interfaces/IGhostPropertyMapper';
import type {
  GhostMutationScope,
  GhostSyncIntent,
  IGhostSyncService,
} from '../core/interfaces/IGhostSyncService';
import type { GhostDefinition, GhostSyncResult } from '../core/types';
import {
  emitReconciliationDiagnostic,
  ReconciliationInvariantMonitor,
  type ReconciliationPhase,
} from '../diagnostics/ReconciliationInvariantMonitor';
import { GhostPositionStrategy } from './GhostPositionStrategy';
import { GhostPropertyMapper } from './GhostPropertyMapper';

// =============================================================================
// Implementation
// =============================================================================

/**
 * Service that synchronizes ghost blocks with required ghost definitions.
 *
 * Uses RECONCILIATION pattern:
 * - Compare required vs existing
 * - Create missing ghosts
 * - Delete obsolete ghosts
 * - Update changed ghosts (ALL properties via mapper.diff())
 */
export class GhostSyncService implements IGhostSyncService {
  private readonly ghostKeyToBlockIds = new Map<string, string[]>();
  private readonly ghostKeyToMutationScope = new Map<
    string,
    GhostMutationScope
  >();
  private readonly invariantMonitor = new ReconciliationInvariantMonitor();
  private readonly propertyMapper: IGhostPropertyMapper;
  private readonly positionStrategy: IGhostPositionStrategy;
  private syncPass = 0;

  constructor(
    private readonly store: Store,
    private readonly rootId: string,
    private readonly getBound: (elementId: string) => Bound | null,
    propertyMapper?: IGhostPropertyMapper,
    positionStrategy?: IGhostPositionStrategy
  ) {
    this.propertyMapper = propertyMapper ?? new GhostPropertyMapper();
    this.positionStrategy = positionStrategy ?? new GhostPositionStrategy();
    this.scanExistingGhosts();
  }

  private scanExistingGhosts(): void {
    this.ghostKeyToBlockIds.clear();
    this.ghostKeyToMutationScope.clear();
    const blocks = this.store.getBlocksByFlavour('affine:ghost');
    for (const block of blocks) {
      const props = block.model?.props as
        | Partial<GhostBlockPropsInput>
        | undefined;
      if (!props?.targetDatabaseId || !block.model?.id) continue;
      const key = this.reconstructKey(props);
      const blockIds = this.ghostKeyToBlockIds.get(key) ?? [];
      blockIds.push(block.model.id);
      blockIds.sort();
      this.ghostKeyToBlockIds.set(key, blockIds);
      if (props.sourceDatabaseId && props.ghostType) {
        this.ghostKeyToMutationScope.set(key, {
          sourceBlockId: props.sourceDatabaseId,
          ghostType: props.ghostType,
        });
      }
    }
  }

  private reconstructKey(props: Partial<GhostBlockPropsInput>): string {
    if (props.ghostType === 'automation' && props.isTargetInTrash) {
      return `trashed-auto-${props.targetDatabaseId}`;
    }
    return `${props.sourceDatabaseId}-${props.targetDatabaseId}`;
  }

  sync(
    required: GhostDefinition[],
    intent: GhostSyncIntent = { kind: 'observe' }
  ): GhostSyncResult {
    this.syncPass++;
    this.scanExistingGhosts();

    const requiredByKey = new Map<string, GhostDefinition>();
    for (const definition of required) {
      if (!requiredByKey.has(definition.key)) {
        requiredByKey.set(definition.key, definition);
      }
    }
    const requiredKeys = new Set(requiredByKey.keys());
    const created: string[] = [];
    const deleted: string[] = [];
    const updated: string[] = [];
    const protectedKeys: string[] = [];
    const phase: ReconciliationPhase = this.store.readonly
      ? 'readonly'
      : intent.kind === 'user-change'
        ? 'mutating'
        : 'observing';

    // Passive discovery, initial document load, remote collaboration updates,
    // and readonly history/capture stores are unconditionally observation-only.
    // Time and scan count never grant permission to mutate the persisted Y.Doc.
    if (phase !== 'mutating') {
      for (const key of this.ghostKeyToBlockIds.keys()) {
        if (!requiredKeys.has(key)) protectedKeys.push(key);
      }
      this.reportDiagnostic(
        required.map(definition => definition.key),
        created,
        deleted,
        updated,
        protectedKeys,
        phase
      );
      return {
        created,
        deleted,
        unchanged: requiredByKey.size,
      };
    }

    const allowedScopes = new Set(
      intent.kind === 'user-change'
        ? intent.scopes.map(scope => this.serializeScope(scope))
        : []
    );
    const scopedRequiredByKey = new Map(
      Array.from(requiredByKey).filter(([, definition]) =>
        allowedScopes.has(
          this.serializeScope(this.getDefinitionMutationScope(definition))
        )
      )
    );

    // A persistent reconciliation is legal only for the exact local source
    // block that the user changed. Workspace discovery is a partial working-set
    // index; therefore unrelated keys can never be deleted by this comparison.
    for (const key of this.ghostKeyToBlockIds.keys()) {
      const existingScope = this.ghostKeyToMutationScope.get(key);
      if (
        existingScope &&
        allowedScopes.has(this.serializeScope(existingScope)) &&
        !scopedRequiredByKey.has(key)
      ) {
        const blockIds = this.ghostKeyToBlockIds.get(key) ?? [];
        for (const blockId of blockIds) {
          this.store.deleteBlock(blockId);
        }
        this.ghostKeyToBlockIds.delete(key);
        deleted.push(key);
      }
    }

    // Create missing keys and update changed metadata. All calls here have a
    // user-change intent; passive metadata arrival cannot enter this branch.
    for (const def of scopedRequiredByKey.values()) {
      const existingBlockId = this.ghostKeyToBlockIds.get(def.key)?.[0];
      if (!existingBlockId) {
        const blockId = this.createGhost(def);
        if (blockId) {
          this.ghostKeyToBlockIds.set(def.key, [blockId]);
          created.push(def.key);
        }
      } else {
        if (this.updateGhostIfNeeded(existingBlockId, def)) {
          updated.push(def.key);
        }
      }
    }

    this.reportDiagnostic(
      required.map(definition => definition.key),
      created,
      deleted,
      updated,
      protectedKeys,
      phase
    );

    return {
      created,
      deleted,
      unchanged: scopedRequiredByKey.size - created.length - updated.length,
    };
  }

  private getDefinitionMutationScope(
    definition: GhostDefinition
  ): GhostMutationScope {
    return {
      sourceBlockId: definition.sourceBlockId,
      ghostType:
        definition.type === 'automation-incoming' ? 'automation' : 'database',
    };
  }

  private serializeScope(scope: GhostMutationScope): string {
    return `${scope.sourceBlockId}\u0000${scope.ghostType}`;
  }

  private reportDiagnostic(
    requiredKeys: readonly string[],
    created: readonly string[],
    deleted: readonly string[],
    updated: readonly string[],
    protectedKeys: readonly string[],
    phase: ReconciliationPhase
  ): void {
    emitReconciliationDiagnostic(
      this.invariantMonitor.inspect({
        reconciler: 'ghost-sync',
        docId: this.store.id,
        phase,
        pass: this.syncPass,
        existingByKey: this.ghostKeyToBlockIds,
        requiredKeys,
        created,
        deleted,
        updated,
        protectedKeys,
      })
    );
  }

  private createGhost(def: GhostDefinition): string | null {
    const context = this.createPositionContext();
    const { position } = this.positionStrategy.calculate(def, context);
    const props = this.propertyMapper.toBlockProps(def, position);
    const blockId = this.store.addBlock(
      'affine:ghost',
      props as unknown as Record<string, unknown>,
      this.rootId
    );
    return blockId ?? null;
  }

  private updateGhostIfNeeded(blockId: string, def: GhostDefinition): boolean {
    const block = this.store.getBlock(blockId);
    if (!block?.model) return false;

    const currentProps = block.model.props as Partial<GhostBlockPropsInput>;

    // Check for trash transition (need position recalculation)
    const wasInTrash = currentProps.isTargetInTrash ?? false;
    const nowInTrash = def.isTargetInTrash ?? false;
    const isTrashTransition = !wasInTrash && nowInTrash;

    // Calculate position
    let position = this.parseCurrentPosition(currentProps.xywh);
    if (isTrashTransition && def.originalPosition) {
      const newPos = this.positionStrategy.forTrashTransition(def);
      if (newPos) position = newPos;
    }

    // Get desired props and diff
    const desiredProps = this.propertyMapper.toBlockProps(def, position);
    const changes = this.propertyMapper.diff(currentProps, desiredProps);

    if (changes) {
      this.store.updateBlock(blockId, changes as Record<string, unknown>);
      console.log(
        `[GhostSync] Updated ghost ${def.key}:`,
        Object.keys(changes)
      );
      return true;
    }
    return false;
  }

  private parseCurrentPosition(xywh?: string): { x: number; y: number } {
    const parsed = parseXYWH(xywh);
    if (parsed) return { x: parsed[0], y: parsed[1] };
    return { x: 100, y: 100 };
  }

  private createPositionContext(): PositionContext {
    return {
      getBound: this.getBound,
      existingGhosts: new Map(
        Array.from(this.ghostKeyToBlockIds, ([key, blockIds]) => [
          key,
          blockIds[0],
        ])
      ),
    };
  }

  getExistingGhostKeys(): string[] {
    this.scanExistingGhosts();
    return Array.from(this.ghostKeyToBlockIds.keys());
  }

  hasGhost(key: string): boolean {
    this.scanExistingGhosts();
    return this.ghostKeyToBlockIds.has(key);
  }

  dispose(): void {
    this.ghostKeyToBlockIds.clear();
    this.ghostKeyToMutationScope.clear();
  }
}
